import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import cn from "classnames";
import { validateEmail } from "../../../../utils/checkEmail";
import stylesCommon from "../../common.module.scss"
import styles from "../../RegistrationPageNew/page.module.scss";
import CustomInput from "../../../custom/custom-input/CustomInput";
import CustomButton from "../../../custom/custom-button/CustomButton";
import CustomSwitch from "../../../custom/custom-switch/CustomSwitch";
import CloseIcon from "../../../shared/icons/CloseIcon";
import { User } from "../../../../services";
import { toast } from "react-toastify";
import { requestErrorDisplay } from "../../../../utils";
import { validatePhoneNumber, validateUserName, parseFullName } from "../../../../utils/registrationValidation";
import CustomPhoneInput from "../../../custom/custom-phone-input/CustomPhoneInput";

const FormRegistration = ({ data, updateStep, setData }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const [userName, setUserName] = useState(`${data.surname || ''} ${data.name || ''} ${data.patronymic || ''}`.trim());
  const [phoneNumber, setPhoneNumber] = useState(data?.phoneNumber || '');
  const [phoneCountryCode, setPhoneCountryCode] = useState(data?.phoneCountryCode || null);
  const [email, setEmail] = useState(data?.email || '');
  const [password, setPassword] = useState(data?.password || '');
  const [isChecked, setIsChecked] = useState(data?.isChecked || false);
  const [isCheckedEmail, setIsCheckedEmail] = useState(true);
  const [isCheckedPhone, setIsCheckedPhone] = useState(true);

  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [patronymic, setPatronymic] = useState('');

  const [errorTitleName, setErrorTitleName] = useState();
  const [errorTitlePhone, setErrorTitlePhone] = useState();
  const [errorTitleEmail, setErrorTitleEmail] = useState();
  const [errorTitlePassword, setErrorTitlePassword] = useState();
  const [errorIsChecked, setErrorIsChecked] = useState();

  useEffect(() => {
    if (data.phoneNumber) setPhoneNumber(data.phoneNumber);
    if (data.email) setEmail(data.email);
    if (data.surname || data.name || data.patronymic) {
      const fullName = `${data.surname || ''} ${data.name || ''} ${data.patronymic || ''}`.trim();
      setUserName(fullName);
    }
  }, [data]);

  useEffect(() => {
    if (!userName) return;
    const validationResult = validateUserName(userName, t);

    if (validationResult.isValid) {
      const { surname, name, patronymic } = parseFullName(userName);
      setSurname(surname);
      setName(name);
      setPatronymic(patronymic);
      setErrorTitleName(null);
    } else {
      setErrorTitleName(validationResult.errorMessage);
    }
  }, [userName]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has('userName')) {
      setUserName(urlParams.get('userName'));
    }
    if (urlParams.has('phoneNumber')) {
      setPhoneNumber(urlParams.get('phoneNumber'));
    }
    if (urlParams.has('email')) {
      setEmail(urlParams.get('email'));
    }
  }, []);

  const validateData = async () => {
    setErrorTitleName(null);
    setErrorTitleEmail(null);
    setErrorTitlePhone(null);
    setErrorTitlePassword(null);
    setErrorIsChecked(null);

    if (!userName) {
      setErrorTitleName(t('registrationForm.validation.nameRequired'));
      return;
    }
    
    const nameValidation = validateUserName(userName, t);
    if (!nameValidation.isValid) {
      setErrorTitleName(nameValidation.errorMessage);
      return;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      setErrorTitlePhone(t('registrationForm.validation.invalidPhoneFormat'));
      return;
    }

    const trimmedEmail = email.trim();
    if (!validateEmail(trimmedEmail)) {
      setErrorTitleEmail(t('registrationForm.validation.invalidEmailFormat'));
      return;
    }

    if (!password) {
      setErrorTitlePassword(t('registrationForm.validation.passwordRequired'));
      return;
    }
    if (password.length < 4) {
      setErrorTitlePassword(t('registrationForm.validation.weakPassword'));
      return;
    }
    if (!isChecked) {
      toast.error(t('registrationForm.toast.termsRequired'));
      setErrorIsChecked(t('registrationForm.validation.termsRequired'));
      return;
    }

    let formattedPhoneNumber = phoneNumber.replace(/\D/g, '');
    const isPhoneValid = await checkPhone(formattedPhoneNumber);
    if (!isPhoneValid) return;

    const isEmailValid = await checkEmail(trimmedEmail);
    if (!isEmailValid) return;

    if (isChecked) {
      setData({
        ...data,
        name: name,
        surname: surname,
        patronymic: patronymic,
        phoneNumber: formattedPhoneNumber,
        phoneCountryCode: phoneCountryCode,
        email: trimmedEmail,
        password: password,
        isChecked: isChecked,
      });
      setIsOpenModal(true);
    }
  };

  const checkEmail = async (email) => {
    try {
      const response = await User.check("email", email);
      if (response.status === 200 && response.data.isExists) {
        toast.error(t('registrationForm.toast.emailExists'));
        setErrorTitleEmail(t('registrationForm.toast.emailExists'));
        setIsCheckedEmail(false);
        return false;
      }
      setIsCheckedEmail(true);
      return true;
    } catch (error) {
      requestErrorDisplay(error, t('registrationForm.toast.genericError'));
      return false;
    }
  };

  const checkPhone = async (phoneNumber) => {
    try {
      const response = await User.check("phoneNumber", phoneNumber);
      if (response.status === 200 && response.data.isExists) {
        toast.error(t('registrationForm.toast.phoneExists'));
        setErrorTitlePhone(t('registrationForm.toast.phoneExists'));
        setIsCheckedPhone(false);
        return false;
      }
      setIsCheckedPhone(true);
      return true;
    } catch (error) {
      requestErrorDisplay(error, t('registrationForm.toast.genericError'));
      return false;
    }
  };

  const handleToggle = (newState) => {
    setIsChecked(newState);
  };

  const nextStep = () => {
    if (!isCheckedEmail || !isCheckedPhone) {
      setIsOpenModal(false);
      return;
    } else updateStep(2);
  }

  const handleClose = () => {
    setIsOpenModal(false);
  }

  return (
    <>
      <form className={styles.card_form}>
        <CustomInput
          placeholder={t('registrationForm.name.placeholder')}
          labelText={t('registrationForm.name.label')}
          inputType="text"
          onChangeValue={setUserName}
          value={userName}
          errorText={errorTitleName}
        />
        <CustomPhoneInput
          labelText={t('registrationForm.phone.label')}
          placeholder={t('driversCreatePage.validation.invalidPhone')}
          value={phoneNumber}
          onChangeValue={setPhoneNumber}
          onCountryChange={setPhoneCountryCode}
          errorText={errorTitlePhone}
          isDeleteText
        />
        <CustomInput
          placeholder={t('registrationForm.email.placeholder')}
          labelText={t('registrationForm.email.label')}
          inputType="text"
          onChangeValue={setEmail}
          value={email}
          errorText={errorTitleEmail} />
        <CustomInput
          placeholder={t('registrationForm.password.placeholder')}
          labelText={t('registrationForm.password.label')}
          inputType="text"
          isPasswordEye={true}
          onChangeValue={setPassword}
          value={password}
          errorText={errorTitlePassword}
        />

        <div className={styles.card_offer}>
          <div className={styles.card_text}>
            <span>
              {t('registrationForm.terms.prefix')}<a href="https://saas.biny.co//offer-agreement.pdf" target="_blank" rel="noopener noreferrer">{t('registrationForm.terms.link')}</a>
            </span>
            <CustomSwitch onToggle={handleToggle} defaultValue={isChecked} />
          </div>
          { errorIsChecked && <div className={styles.errorText}>{errorIsChecked}</div> }
        </div>
      </form>

      <div className={styles.btns}>
        <CustomButton isFall text={t('registrationForm.buttons.continue')} onClick={validateData} isLoading={isLoading} />
        <div className={styles.btns_text}>
          <div className={styles.btns_before}></div>
          <div className={styles.btns_textDashes}>{t('registrationForm.login.prompt')}</div>
          <div className={styles.btns_before}></div>
        </div>
        <CustomButton isFall text={t('registrationForm.buttons.login')} isWhite={true} onClick={() => navigate('/login')} />
      </div>

      {isOpenModal &&
        <div className={cn("modal-overlay", stylesCommon.modal)}>
          <div className={cn(stylesCommon.modal_content)}>
            <div className={stylesCommon.modal_text}>
              <div className={stylesCommon.modal_title}>{t('registrationForm.modal.title')} <CloseIcon fill={'#6D7885'} onClick={handleClose} /> </div>
              <div className={stylesCommon.modal_description}>{t('registrationForm.modal.description')}</div>
            </div>
            <CustomInput placeholder={t('registrationForm.email.placeholder')} labelText={t('registrationForm.email.label')} inputType="text" onChangeValue={setEmail} value={email} errorText={errorTitleEmail} isDisabled />
            <div className={stylesCommon.modal_btns}>
              <CustomButton isFall text={t('registrationForm.modal.confirmButton')} onClick={nextStep} />
              <CustomButton isFall text={t('registrationForm.modal.changeButton')} isUnderline onClick={handleClose} />
            </div>
          </div>
        </div>
      }
    </>

  );
};

export default FormRegistration;
